/*
 * SWAG BUDS: storefront behaviour
 * --------------------------------------------------------------------------
 * Loaded as an ES module so it can use Horizon's import map (@shopify/events).
 * Add-to-bag dispatches the same CartLinesUpdateEvent Horizon's own product
 * form uses, so the Horizon cart drawer refreshes/opens itself and Shopify's
 * standard analytics events (pixels) keep working.
 * Everything degrades: without JS the product form posts to /cart/add.
 */

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/* ---------------------------------------------------------------- helpers */

let eventsModule = null;
function loadEvents() {
  if (!eventsModule) {
    eventsModule = import('@shopify/events').catch((error) => {
      console.warn('[swag-buds] @shopify/events unavailable; using /cart fallback', error);
      return null;
    });
  }
  return eventsModule;
}

function route(key, fallbackPath) {
  try {
    // `Theme` is a global const declared by Horizon in snippets/scripts.liquid.
    // eslint-disable-next-line no-undef
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes[key]) return Theme.routes[key];
  } catch (error) {
    /* ignore */
  }
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root + fallbackPath;
}

const cartUrl = () => route('cart_url', 'cart');
const cartAddUrl = () => route('cart_add_url', 'cart/add.js');
const scrollBehavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

function getCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  return drawer && typeof drawer.open === 'function' ? drawer : null;
}

function openCart() {
  const drawer = getCartDrawer();
  if (!drawer) return false;
  if (!drawer.isOpen && !drawer.hasAttribute('open')) drawer.open();
  return true;
}

function fetchCart() {
  return fetch(`${cartUrl()}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  });
}

function setCartCount(count) {
  $$('[data-sb-cart-count]').forEach((element) => {
    const hasItems = Number(count) > 0;
    element.textContent = hasItems ? `(${count})` : '';
    element.hidden = !hasItems;
  });
}

/* Any cart change anywhere (our form, Horizon drawer, apps) updates the count. */
document.addEventListener(CART_LINES_UPDATE, (event) => {
  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') return;
  promise
    .then((result) => {
      const count = result && result.cart ? result.cart.totalQuantity : undefined;
      if (typeof count === 'number') setCartCount(count);
    })
    .catch(() => {});
});

function once(element, key) {
  const flag = `sb${key}`;
  if (element.dataset[flag]) return false;
  element.dataset[flag] = '1';
  return true;
}

/* ----------------------------------------------------------------- header */

function initHeader(root) {
  $$('[data-sb-header]', root).forEach((header) => {
    if (!once(header, 'Header')) return;

    const bag = $('[data-sb-bag]', header);
    if (bag && bag.tagName === 'BUTTON') {
      bag.addEventListener('click', (event) => {
        const drawer = getCartDrawer();
        if (drawer) {
          event.preventDefault();
          if (typeof drawer.toggle === 'function') drawer.toggle();
          else drawer.open();
          return;
        }
        if (window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart) {
          window.Shopify.actions.openCart();
          return;
        }
        window.location.href = cartUrl();
      });

      const drawerElement = document.getElementById('cart-drawer');
      if (drawerElement && 'MutationObserver' in window) {
        const sync = () => bag.setAttribute('aria-expanded', drawerElement.hasAttribute('open') ? 'true' : 'false');
        new MutationObserver(sync).observe(drawerElement, { attributes: true, attributeFilter: ['open'] });
        sync();
      }
    }

    const announcement = document.querySelector('[data-sb-announce]');
    const setOffsets = () => {
      const headerHeight = Math.round(header.getBoundingClientRect().height);
      const announceHeight = announcement ? Math.round(announcement.getBoundingClientRect().height) : 0;
      const rootStyle = document.documentElement.style;
      rootStyle.setProperty('--sb-header-offset', `${headerHeight}px`);
      rootStyle.setProperty('--sb-chrome-h', `${headerHeight + announceHeight}px`);
      document.body.style.setProperty('--header-height', `${headerHeight}px`);
    };
    setOffsets();
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(setOffsets);
      observer.observe(header);
      if (announcement) observer.observe(announcement);
    }

    // Horizon scrolls .page-wrapper on desktop and the window on mobile.
    const wrapper = document.querySelector('.page-wrapper');
    const onScroll = () => {
      const y = Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
      header.classList.toggle('is-scrolled', y > 4);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });
}

function initAnnouncements(root) {
  $$('[data-sb-announce]', root).forEach((bar) => {
    if (!once(bar, 'Announce')) return;
    const messages = $$('[data-sb-announce-msg]', bar);
    if (messages.length < 2 || reduceMotion.matches) return;
    let index = 0;
    window.setInterval(() => {
      if (!bar.isConnected) return;
      messages[index].hidden = true;
      index = (index + 1) % messages.length;
      messages[index].hidden = false;
    }, 4500);
  });
}

/* ----------------------------------------------------------------- video */

const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const visible = entry.isIntersecting && video.offsetParent !== null;
            if (visible && !reduceMotion.matches) {
              if (!video.dataset.sbSound) video.muted = true;
              const attempt = video.play();
              if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: 0.35 }
      )
    : null;

function initVideos(root) {
  $$('[data-sb-video] video', root).forEach((video) => {
    if (!once(video, 'Video')) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    if (reduceMotion.matches) {
      video.removeAttribute('autoplay');
      video.pause();
      video.controls = true;
    }
    if (videoObserver) videoObserver.observe(video);
  });

  $$('[data-sb-sound]', root).forEach((button) => {
    if (!once(button, 'Sound')) return;
    button.addEventListener('click', () => {
      const clip = button.closest('[data-sb-clip]');
      const video = clip ? $('video', clip) : null;
      if (!video) return;
      const turnOn = video.muted;
      $$('[data-sb-clip]').forEach((other) => {
        const otherVideo = $('video', other);
        const otherButton = $('[data-sb-sound]', other);
        if (!otherVideo || otherVideo === video) return;
        otherVideo.muted = true;
        delete otherVideo.dataset.sbSound;
        if (otherButton) otherButton.setAttribute('aria-pressed', 'false');
      });
      video.muted = !turnOn;
      if (turnOn) {
        video.dataset.sbSound = '1';
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      } else {
        delete video.dataset.sbSound;
      }
      button.setAttribute('aria-pressed', turnOn ? 'true' : 'false');
    });
  });
}

/* ------------------------------------------------------------------ rails */

function initRails(root) {
  $$('[data-sb-rail]', root).forEach((rail) => {
    if (!once(rail, 'Rail')) return;
    const track = $('[data-sb-rail-track]', rail);
    if (!track) return;
    const prev = $('[data-sb-rail-prev]', rail);
    const next = $('[data-sb-rail-next]', rail);

    const step = () => {
      const first = track.firstElementChild;
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      rail.classList.toggle('is-static', max <= 0);
    };

    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: scrollBehavior() }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: scrollBehavior() }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  });
}

/* --------------------------------------------------------- buy module */

function initBuy(root) {
  $$('[data-sb-buy]', root).forEach((buy) => {
    if (!once(buy, 'Buy')) return;

    const form = $('form[data-sb-product-form]', buy);
    const dataElement = $('script[data-sb-variants]', buy);
    if (!form || !dataElement) return;

    let variants = [];
    try {
      variants = JSON.parse(dataElement.textContent || '[]');
    } catch (error) {
      variants = [];
    }

    const scope = buy.closest('[data-sb-product-scope]') || buy;
    const idInput = $('[data-sb-variant-id]', form);
    const addButton = $('[data-sb-add]', form);
    const addLabel = $('[data-sb-add-label]', form);
    const errorElement = $('[data-sb-error]', buy);
    const liveRegion = $('[data-sb-live]', buy);
    const groups = $$('[data-sb-option]', buy);
    const labels = {
      add: buy.dataset.labelAdd || 'Add to bag',
      soldOut: buy.dataset.labelSoldout || 'Sold out',
      unavailable: buy.dataset.labelUnavailable || 'Unavailable',
      added: buy.dataset.labelAdded || 'Added to bag',
      error: buy.dataset.labelError || 'Something went wrong. Try again.',
    };

    const selectedValues = () =>
      groups.map((group) => {
        const checked = $('input:checked', group);
        return checked ? checked.value : null;
      });

    const compatible = (variant, values) => variant.options.every((option, i) => values[i] == null || option === values[i]);
    const findVariant = (values) => variants.find((variant) => variant.options.every((option, i) => option === values[i]));

    const showError = (message) => {
      if (!errorElement) return;
      errorElement.textContent = message;
      errorElement.hidden = false;
    };

    const hideError = () => {
      if (!errorElement) return;
      errorElement.textContent = '';
      errorElement.hidden = true;
    };

    const announce = (message) => {
      if (!liveRegion) return;
      liveRegion.textContent = message;
      window.setTimeout(() => {
        liveRegion.textContent = '';
      }, 4000);
    };

    const expressElement = $('[data-sb-express]', form);
    const setExpress = (enabled) => {
      if (!expressElement) return;
      expressElement.inert = !enabled;
      expressElement.classList.toggle('is-locked', !enabled);
    };

    const missingGroups = (values) => groups.filter((group, index) => values[index] == null);

    // Sticky bar buttons mirror the main button's state (label + enabled).
    const setSticky = (text, disabled) => {
      $$('[data-sb-sticky-add]', scope).forEach((button) => {
        button.disabled = disabled;
        const label = $('[data-sb-sticky-label]', button);
        if (label) label.textContent = text;
        else button.textContent = text;
      });
    };

    function applyVariant(variant, fromUser) {
      if (!variant) {
        idInput.value = '';
        const values = selectedValues();
        const missing = missingGroups(values);
        if (missing.length) {
          // Not a dead end: the button stays clickable and points at the missing choice.
          const prompt = missing.length === 1 ? missing[0].dataset.sbPrompt || labels.unavailable : labels.unavailable;
          const short = missing.length === 1 ? missing[0].dataset.sbPromptShort || prompt : labels.unavailable;
          addButton.disabled = false;
          addButton.setAttribute('aria-disabled', 'true');
          addButton.classList.add('is-prompt');
          addLabel.textContent = prompt;
          const picked = values.filter(Boolean).join(' / ');
          $$('[data-sb-variant-title]', scope).forEach((element) => {
            element.textContent = picked || short;
          });
          setSticky(short, false);
        } else {
          // Every option picked, but that combination doesn't exist.
          addButton.disabled = true;
          addButton.removeAttribute('aria-disabled');
          addButton.classList.remove('is-prompt');
          addLabel.textContent = 'Unavailable';
          setSticky('Unavailable', true);
        }
        setExpress(false);
        return;
      }

      idInput.value = String(variant.id);
      // Let Shopify's accelerated checkout see the new variant id.
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      $$('[data-sb-price]', scope).forEach((element) => {
        element.textContent = variant.price;
      });
      $$('[data-sb-compare]', scope).forEach((element) => {
        element.textContent = variant.on_sale ? variant.compare : '';
        element.hidden = !variant.on_sale;
      });
      $$('[data-sb-variant-title]', scope).forEach((element) => {
        element.textContent = variant.title;
      });
      addButton.classList.remove('is-prompt');
      addButton.removeAttribute('aria-disabled');
      addButton.disabled = !variant.available;
      addLabel.textContent = variant.available ? labels.add : labels.soldOut;
      setSticky(variant.available ? labels.add : labels.soldOut, !variant.available);
      setExpress(variant.available);

      if (fromUser && buy.dataset.updateUrl === 'true' && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url.toString());
      }

      if (fromUser && variant.media) {
        scope.dispatchEvent(new CustomEvent('sb:media-select', { detail: { mediaId: String(variant.media) } }));
      }

      // Homepage shop card: swap the product shot to the chosen finish.
      if (fromUser && variant.image) {
        const cardImage = $('.sb-shop__card > .sb-media img', scope);
        if (cardImage) {
          cardImage.removeAttribute('srcset');
          cardImage.src = variant.image;
        }
      }
    }

    function refresh(fromUser) {
      const values = selectedValues();
      groups.forEach((group, groupIndex) => {
        const output = $('[data-sb-option-value]', group);
        if (output) output.textContent = values[groupIndex] || '';
        $$('input', group).forEach((input) => {
          const test = values.slice();
          test[groupIndex] = input.value;
          const possible = variants.some((variant) => variant.available && compatible(variant, test));
          const pill = input.closest('.sb-pill');
          if (pill) pill.classList.toggle('is-unavailable', !possible);
        });
      });
      if (groups.length) applyVariant(findVariant(values), fromUser);
      if (fromUser) groups.forEach((group) => group.classList.remove('is-flagged'));
      hideError();
    }

    // Add to bag clicked before every option is picked: point at what's missing.
    let flagTimer = null;
    function promptMissing() {
      const target = missingGroups(selectedValues())[0];
      if (!target) return;
      groups.forEach((group) => group.classList.toggle('is-flagged', group === target));
      window.clearTimeout(flagTimer);
      flagTimer = window.setTimeout(() => target.classList.remove('is-flagged'), 2400);
      const rect = target.getBoundingClientRect();
      if (rect.top < 90 || rect.bottom > window.innerHeight - 90) {
        target.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      }
      const firstInput = $('input', target);
      if (firstInput) firstInput.focus({ preventScroll: true });
      const message = `${target.dataset.sbPrompt || 'Choose an option'} to continue.`;
      showError(message);
      announce(message);
    }

    $$('[data-sb-guide-toggle]', buy).forEach((toggle) => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
      });
    });

    groups.forEach((group) => group.addEventListener('change', () => refresh(true)));
    refresh(false);

    let busy = false;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy) return;

      const formData = new FormData(form);
      const variantId = formData.get('id');
      if (!variantId) {
        promptMissing();
        return;
      }
      if (addButton.disabled) return;

      busy = true;
      addButton.classList.add('is-loading');
      addButton.setAttribute('aria-busy', 'true');
      hideError();

      const quantity = Number(formData.get('quantity')) || 1;
      const sectionIds = [
        ...new Set(
          $$('cart-items-component[data-section-id]')
            .map((element) => element.dataset.sectionId)
            .filter(Boolean)
        ),
      ];
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const events = await loadEvents();
      let deferred = null;
      if (events && events.CartLinesUpdateEvent) {
        try {
          deferred = events.CartLinesUpdateEvent.createPromise();
          form.dispatchEvent(
            new events.CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: [{ merchandiseId: String(variantId), quantity }],
              promise: deferred.promise,
            })
          );
        } catch (error) {
          deferred = null;
        }
      }

      try {
        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: formData,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          const message = data.description || data.message || labels.error;
          showError(message);
          if (deferred) {
            const cart = await fetchCart().catch(() => null);
            if (cart) {
              deferred.resolve({
                cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { didError: true, items: cart.items, itemCount: quantity, source: 'sb-product-form' },
              });
            } else {
              deferred.reject(new Error(message));
            }
            if (events.CartErrorEvent) {
              form.dispatchEvent(
                new events.CartErrorEvent({ error: message, code: 'INVALID', detail: { description: data.description } })
              );
            }
          }
          return;
        }

        const cart = await fetchCart();
        setCartCount(cart.item_count);

        if (deferred) {
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: false,
              items: cart.items,
              sections: data.sections,
              itemCount: quantity,
              source: 'sb-product-form',
              productId: buy.dataset.productId,
            },
          });
          announce(labels.added);
          window.setTimeout(() => {
            if (!openCart()) window.location.href = cartUrl();
          }, 60);
        } else {
          window.location.href = cartUrl();
        }
      } catch (error) {
        console.error('[swag-buds] add to bag failed', error);
        if (deferred) deferred.reject(error);
        showError(labels.error);
      } finally {
        busy = false;
        addButton.classList.remove('is-loading');
        addButton.removeAttribute('aria-busy');
      }
    });
  });
}

/* ----------------------------------------------------- sticky add to bag */

function initSticky(root) {
  $$('[data-sb-sticky]', root).forEach((bar) => {
    if (!once(bar, 'Sticky')) return;
    const scope = bar.closest('[data-sb-product-scope]');
    const form = scope ? $('form[data-sb-product-form]', scope) : null;
    const mainButton = scope ? $('[data-sb-add]', scope) : null;
    const stickyButton = $('[data-sb-sticky-add]', bar);
    if (!form || !mainButton || !stickyButton || !('IntersectionObserver' in window)) return;

    // Missing a choice (e.g. connector)? Pick it right in the bar instead of
    // being scrolled back up the page.
    let picker = null;
    const closePicker = () => {
      if (!picker) return;
      picker.hidden = true;
      stickyButton.setAttribute('aria-expanded', 'false');
    };
    const openPicker = () => {
      const buy = form.closest('[data-sb-buy]') || scope;
      const missing = $$('[data-sb-option]', buy).find((group) => !$('input:checked', group));
      if (!missing) return false;
      const inputs = $$('input[type="radio"]', missing);
      if (!inputs.length) return false;

      if (!picker) {
        picker = document.createElement('div');
        picker.className = 'sb-sticky__picker';
        picker.setAttribute('role', 'group');
        picker.dataset.sbStickyPicker = '';
        bar.insertBefore(picker, bar.firstElementChild);
      }
      picker.replaceChildren();
      const prompt = missing.dataset.sbPrompt || 'Choose an option';
      picker.setAttribute('aria-label', prompt);
      const title = document.createElement('p');
      title.className = 'sb-label sb-sticky__prompt';
      title.textContent = prompt;
      const row = document.createElement('div');
      row.className = 'sb-sticky__opts';
      inputs.forEach((input) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'sb-sticky__opt';
        option.textContent = input.value;
        const pill = input.closest('.sb-pill');
        if (pill && pill.classList.contains('is-unavailable')) option.disabled = true;
        option.addEventListener('click', () => {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          closePicker();
          // Another choice still missing? Ask for that one next.
          if (mainButton.classList.contains('is-prompt')) openPicker();
          else stickyButton.focus({ preventScroll: true });
        });
        row.appendChild(option);
      });
      picker.append(title, row);
      picker.hidden = false;
      stickyButton.setAttribute('aria-expanded', 'true');
      const first = $('.sb-sticky__opt:not([disabled])', row);
      if (first) first.focus({ preventScroll: true });
      return true;
    };

    stickyButton.addEventListener('click', () => {
      if (mainButton.disabled) {
        mainButton.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
        return;
      }
      if (mainButton.classList.contains('is-prompt')) {
        if (picker && !picker.hidden) {
          closePicker();
          return;
        }
        if (openPicker()) return;
      }
      if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
      else mainButton.click();
    });

    let mainVisible = true;
    let mainAbove = false;
    let footerVisible = false;
    const render = () => {
      const show = !mainVisible && mainAbove && !footerVisible;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (!show) closePicker();
    };

    new IntersectionObserver(([entry]) => {
      mainVisible = entry.isIntersecting;
      mainAbove = entry.boundingClientRect.top < 0;
      render();
    }).observe(mainButton);

    const footer = document.querySelector('[data-sb-footer]');
    if (footer) {
      new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        render();
      }).observe(footer);
    }

    bar.hidden = false;
    render();
  });
}

/* ------------------------------------------ remote add (final purchase) */

// A second Add to bag further down the page. It never keeps its own state:
// it mirrors the main button (label, price, enabled) and submits the main
// form, so the variant, price and availability always match.
function initRemoteAdd(root) {
  $$('[data-sb-remote-add]', root).forEach((remote) => {
    if (!once(remote, 'Remote')) return;
    const productId = remote.dataset.productId;
    const buy = productId
      ? document.querySelector(`[data-sb-buy][data-product-id="${productId}"]`)
      : document.querySelector('[data-sb-buy]');
    const form = buy ? $('form[data-sb-product-form]', buy) : null;
    const mainButton = form ? $('[data-sb-add]', form) : null;
    const button = $('[data-sb-remote-button]', remote);
    if (!buy || !form || !mainButton || !button) {
      remote.hidden = true;
      return;
    }
    const label = $('[data-sb-remote-label]', remote);
    const price = $('[data-sb-remote-price]', remote);
    const selection = $('[data-sb-remote-selection]', remote);
    const mainLabel = $('[data-sb-add-label]', mainButton);
    const mainPrice = $('[data-sb-price]', mainButton);

    const sync = () => {
      if (label && mainLabel) label.textContent = mainLabel.textContent;
      if (price && mainPrice) price.textContent = mainPrice.textContent;
      button.disabled = mainButton.disabled;
      button.classList.toggle('is-prompt', mainButton.classList.contains('is-prompt'));
      button.classList.toggle('is-loading', mainButton.classList.contains('is-loading'));
      if (mainButton.hasAttribute('aria-disabled')) button.setAttribute('aria-disabled', mainButton.getAttribute('aria-disabled'));
      else button.removeAttribute('aria-disabled');
      if (selection) {
        const picked = $$('[data-sb-option] input:checked', buy).map((input) => input.value);
        selection.textContent = picked.join(' / ');
        selection.hidden = picked.length === 0;
      }
    };

    sync();
    new MutationObserver(sync).observe(mainButton, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
    buy.addEventListener('change', sync);

    button.addEventListener('click', () => {
      if (mainButton.disabled) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
      else mainButton.click();
    });
  });
}

/* ---------------------------------------------------------------- gallery */

function initGallery(root) {
  $$('[data-sb-gallery]', root).forEach((gallery) => {
    if (!once(gallery, 'Gallery')) return;
    const track = $('[data-sb-gallery-track]', gallery);
    const slides = $$('[data-sb-slide]', gallery);
    if (!track || !slides.length) return;
    const current = $('[data-sb-gallery-current]', gallery);
    const progress = $('[data-sb-gallery-progress]', gallery);
    const total = slides.length;

    const thumbs = $$('[data-sb-thumb]', gallery);

    const isCarousel = () => track.scrollWidth > track.clientWidth + 2;

    const setActive = (index) => {
      thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };

    const update = () => {
      if (!isCarousel()) return;
      const width = slides[0].getBoundingClientRect().width || track.clientWidth;
      const index = Math.min(total - 1, Math.max(0, Math.round(track.scrollLeft / width)));
      if (current) current.textContent = String(index + 1);
      if (progress) progress.style.setProperty('--p', String((index + 1) / total));
      setActive(index);
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const slide = slides[i];
        if (!slide) return;
        setActive(i);
        if (isCarousel()) {
          const padding = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
          track.scrollTo({ left: slide.offsetLeft - padding, behavior: scrollBehavior() });
        } else {
          slide.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
        }
      });
    });
    setActive(0);

    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    update();

    const scope = gallery.closest('[data-sb-product-scope]');
    if (!scope) return;
    scope.addEventListener('sb:media-select', (event) => {
      const target = slides.find((slide) => slide.dataset.mediaId === event.detail.mediaId);
      if (!target) return;
      if (!isCarousel()) {
        // Desktop grid: bring the chosen finish up to the first product-photo spot.
        const firstProduct = $('[data-sb-slide][data-media-id]', track);
        if (firstProduct && firstProduct !== target) track.insertBefore(target, firstProduct);
        return;
      }
      const padding = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
      track.scrollTo({ left: target.offsetLeft - padding, behavior: scrollBehavior() });
    });
  });
}

/* ------------------------------------------------------------------- boot */

function initAll(root = document) {
  initHeader(root);
  initAnnouncements(root);
  initVideos(root);
  initRails(root);
  initBuy(root);
  initSticky(root);
  initRemoteAdd(root);
  initGallery(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

// Theme editor support
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  const track = block.closest('[data-sb-rail-track], [data-sb-gallery-track]');
  if (!track || track.scrollWidth <= track.clientWidth) return;
  const padding = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
  track.scrollTo({ left: block.offsetLeft - padding, behavior: scrollBehavior() });
});